// Phase 6 — React Query hooks for the Аналитика screen.
//
// Three sibling hooks share a single ``queryKey`` namespace
// (``['analytics', kind, filters]``) so the screen can blanket-
// invalidate all of them on a filter change or on the 30-second
// auto-refresh tick via ``queryClient.invalidateQueries({
// queryKey: ['analytics'] })``.

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from '../api/client';
import type {
  AnalyticsActivityResponse,
  AnalyticsFilters,
  AnalyticsHeatmap,
  AnalyticsScatterResponse,
  AnalyticsSummary,
  ConvergenceAggregateResponse,
  ConvergenceCurveResponse,
  ConvergenceHeatmapData,
  ConvergenceSeriesListResponse,
  ProcessingTimeResponse,
  SelfAssessmentResponse,
} from '../api/types';

/** Stable key part — sorts the gnss_configs list so the React Query
 *  cache key matches when the user re-clicks pills in a different
 *  order. ``null``/empty entries collapse to a literal "all" so the
 *  "no filter" view always shares a cache slot. */
function _filterKey(filters: AnalyticsFilters): unknown {
  const sortedConfigs = filters.gnss_configs
    ? [...filters.gnss_configs].sort()
    : null;
  return {
    tectonic_model: filters.tectonic_model ?? null,
    gnss_configs: sortedConfigs && sortedConfigs.length > 0 ? sortedConfigs : null,
  };
}

export function useAnalyticsSummary(filters: AnalyticsFilters) {
  return useQuery<AnalyticsSummary>({
    queryKey: ['analytics', 'summary', _filterKey(filters)],
    queryFn: () => api.getAnalyticsSummary(filters),
    staleTime: 15_000,
    // C1.3 — on a filter change keep the previous cohort visible while the new
    // one loads, so the section never blanks out / unmounts (no empty flash).
    placeholderData: keepPreviousData,
  });
}

export function useAnalyticsHeatmap(filters: AnalyticsFilters) {
  return useQuery<AnalyticsHeatmap>({
    queryKey: ['analytics', 'heatmap', _filterKey(filters)],
    queryFn: () => api.getAnalyticsHeatmap(filters),
    staleTime: 15_000,
    // C1.3 — keep the previous station×config matrix on screen during a filter
    // change instead of unmounting it (no empty flash, no re-mount cost).
    placeholderData: keepPreviousData,
  });
}

export function useAnalyticsScatter(filters: AnalyticsFilters) {
  return useQuery<AnalyticsScatterResponse>({
    queryKey: ['analytics', 'scatter', _filterKey(filters)],
    queryFn: () => api.getAnalyticsScatter(filters),
    staleTime: 15_000,
    // C1.3 — keep the previous scatter (up to 14611 points) mounted through a
    // filter change so it never tears down + rebuilds the SVG on an empty flash.
    placeholderData: keepPreviousData,
  });
}

/** Phase B Block 2 — processing time by config. No filters — the
 *  endpoint is global. Still keyed under the ``['analytics']``
 *  namespace so the screen's 30-s auto-refresh invalidation
 *  (``invalidateQueries({ queryKey: ['analytics'] })``) catches it. */
export function useAnalyticsProcessingTime() {
  return useQuery<ProcessingTimeResponse>({
    queryKey: ['analytics', 'processing-time'],
    queryFn: () => api.getAnalyticsProcessingTime(),
    staleTime: 15_000,
  });
}

/** Phase B Block 3 — service self-assessment adequacy. No filters;
 *  same ``['analytics']`` namespace so auto-refresh covers it. */
export function useAnalyticsSelfAssessment() {
  return useQuery<SelfAssessmentResponse>({
    queryKey: ['analytics', 'self-assessment'],
    queryFn: () => api.getAnalyticsSelfAssessment(),
    staleTime: 15_000,
  });
}

/** Phase I — activity heatmap by completion + observation date.
 *  No filters; same ``['analytics']`` namespace so the 30-s
 *  auto-refresh invalidation catches it. */
export function useAnalyticsActivity() {
  return useQuery<AnalyticsActivityResponse>({
    queryKey: ['analytics', 'activity'],
    queryFn: () => api.getAnalyticsActivity(),
    staleTime: 15_000,
  });
}

/** Phase C-agg — aggregated convergence curve (median |dR| per config)
 *  + honest series breakdown. Optional date bounds (ISO `YYYY-MM-DD`);
 *  Phase 1 passes none (no UI date filter yet). Same ``['analytics']``
 *  namespace so the 30-s auto-refresh invalidation catches it. */
export function useConvergenceAggregate(fromDate?: string, toDate?: string) {
  return useQuery<ConvergenceAggregateResponse>({
    queryKey: ['analytics', 'convergence-aggregate', fromDate ?? null, toDate ?? null],
    queryFn: () => api.getConvergenceAggregate(fromDate, toDate),
    staleTime: 15_000,
    // C1.3 — keep previous data across a date-bound change (no empty flash).
    placeholderData: keepPreviousData,
  });
}

/** Phase C-ui — every convergence series (one card per tag). Under the
 *  ['analytics'] namespace so the shell's 30-s auto-refresh catches it. */
export function useConvergenceSeriesList() {
  return useQuery<ConvergenceSeriesListResponse>({
    queryKey: ['analytics', 'convergence-series'],
    queryFn: () => api.listConvergenceSeries(),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/** Phase C-viz — station×config convergence matrix. Under ['analytics'] so
 *  the auto-refresh tick reaches it (same cohort/bounds as the aggregate). */
export function useConvergenceHeatmap(fromDate?: string, toDate?: string) {
  return useQuery<ConvergenceHeatmapData>({
    queryKey: ['analytics', 'convergence-heatmap', fromDate ?? null, toDate ?? null],
    queryFn: () => api.getConvergenceHeatmap(fromDate, toDate),
    staleTime: 15_000,
    // C1.3 — keep the previous convergence matrix across a date-bound change.
    placeholderData: keepPreviousData,
  });
}

/** Phase C-ui-v2 — per-config curves for ONE series, fetched lazily when a
 *  series card is expanded: one request per config in parallel, keyed by
 *  config (the curve endpoint is per-config and its points carry no config
 *  tag). Cached for the whole session (`staleTime`/`gcTime` Infinity) and
 *  deliberately OUTSIDE the ['analytics'] auto-refresh namespace, so
 *  re-opening a card never refetches — the operator's expand/collapse is free. */
export function useConvergenceCurves(tag: string | null, configs: string[]) {
  const sorted = [...configs].sort();
  return useQuery<Record<string, ConvergenceCurveResponse>>({
    queryKey: ['convergence-curves', tag, sorted],
    enabled: tag != null && sorted.length > 0,
    queryFn: async () => {
      const entries = await Promise.all(
        sorted.map(
          async (config) =>
            [config, await api.getConvergenceCurve(tag as string, config)] as const,
        ),
      );
      return Object.fromEntries(entries);
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
}
